import React from "react";
import Loader from "../../../common/loader";
import Sorting from "../../../common/sorting";
import UsageCell from "./UsageCell";
import type { RestaurantRow, SortState, Translate } from "../chatbotManagement.types";
import { formatDate, formatSummaryCost } from "../chatbotManagement.utils";

interface ChatbotRestaurantTableProps {
  rows: RestaurantRow[];
  loading: boolean;
  search: string;
  accessFilter: string;
  page: number;
  total: number;
  sorting: SortState;
  defaultSorting: boolean;
  updatingId: string | null;
  t: Translate;
  onSearchChange: (value: string) => void;
  onAccessFilterChange: (value: string) => void;
  onSort: (event: unknown, column: string) => void;
  onToggleAccess: (row: RestaurantRow) => void;
  onOpenDetails: (row: RestaurantRow) => void;
  onPageChange: (page: number) => void;
}

const ChatbotRestaurantTable = ({
  rows,
  loading,
  search,
  accessFilter,
  page,
  total,
  sorting,
  defaultSorting,
  updatingId,
  t,
  onSearchChange,
  onAccessFilterChange,
  onSort,
  onToggleAccess,
  onOpenDetails,
  onPageChange,
}: ChatbotRestaurantTableProps): React.ReactElement => {
  const pageCount = Math.max(1, Math.ceil(total / 10));

  return (
    <>
      <div
        className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
        aria-label={t("admin_chatbot_table_controls")}
      >
        <h6 className="mb-0">{t("sidebar_link_restaurants")}</h6>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="form-group mb-0">
            <input
              type="search"
              aria-label={t("admin_chatbot_search_restaurants")}
              className="form-control"
              style={{ minWidth: 220, height: 42 }}
              placeholder={t("admin_chatbot_search_placeholder")}
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
            />
          </div>
          <div className="form-group mb-0">
            <select
              aria-label={t("admin_chatbot_filter_access")}
              className="form-control cp"
              style={{ minWidth: 150, height: 42 }}
              value={accessFilter}
              onChange={(event) => onAccessFilterChange(event.target.value)}
            >
              <option value="">{t("admin_chatbot_all_access")}</option>
              <option value="true">{t("admin_chatbot_enabled")}</option>
              <option value="false">{t("admin_chatbot_disabled")}</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered border-t0 key-buttons text-nowrap w-100">
            <thead>
              <tr>
                <th className="position-relative select_head">{t("list_heading_sno")}</th>
                <th>
                  <div className="sorting_column">
                    <span>{t("sidebar_link_restaurant")}</span>
                    <Sorting
                      sort={sorting}
                      handleSort={onSort}
                      column="restaurant_name"
                      defaultSorting={defaultSorting}
                    />
                  </div>
                </th>
                <th>
                  <div className="sorting_column">
                    <span>{t("admin_chatbot_weekly_usage")}</span>
                    <Sorting
                      sort={sorting}
                      handleSort={onSort}
                      column="tokens_used"
                      defaultSorting={defaultSorting}
                    />
                  </div>
                </th>
                <th>
                  <div className="sorting_column">
                    <span>{t("admin_chatbot_weekly_cost")}</span>
                    <Sorting
                      sort={sorting}
                      handleSort={onSort}
                      column="cost_usd"
                      defaultSorting={defaultSorting}
                    />
                  </div>
                </th>
                <th className="created_head">
                  <div className="sorting_column">
                    <span>{t("admin_chatbot_last_active")}</span>
                    <Sorting
                      sort={sorting}
                      handleSort={onSort}
                      column="last_active_at"
                      defaultSorting={defaultSorting}
                    />
                  </div>
                </th>
                <th className="status_head">{t("admin_chatbot_access")}</th>
                <th className="action_head">{t("admin_chatbot_details")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((row, index) => (
                  <tr key={row.restaurant_id}>
                    <td>{(page - 1) * 10 + index + 1}</td>
                    <td>
                      <strong>{row.restaurant_name}</strong>
                      <br />
                      <small>{row.email}</small>
                    </td>
                    <td><UsageCell row={row} t={t} /></td>
                    <td><strong>{formatSummaryCost(row.weekly_usage?.cost_usd)}</strong></td>
                    <td>{formatDate(row.last_active_at, t)}</td>
                    <td>
                      <label className="custom-switch mb-0">
                        <input
                          type="checkbox"
                          name="custom-switch-checkbox"
                          className="custom-switch-input"
                          aria-label={t("admin_chatbot_access_for", {
                            restaurant: row.restaurant_name,
                          })}
                          checked={row.access_enabled}
                          disabled={updatingId === row.restaurant_id}
                          onChange={() => onToggleAccess(row)}
                        />
                        <span className="custom-switch-indicator cp"></span>
                      </label>
                    </td>
                    <td>
                      <button
                        className="btn ripple btn-main-primary signbtn"
                        onClick={() => onOpenDetails(row)}
                      >
                        {t("btn_view")}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    {t("admin_chatbot_no_restaurants")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mt-3">
        <span>{t("admin_chatbot_restaurant_count", { count: total })}</span>
        <div>
          <button
            className="btn btn-sm btn-light mr-2"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            {t("admin_chatbot_previous")}
          </button>
          <span>{t("admin_chatbot_page_of", { page, total: pageCount })}</span>
          <button
            className="btn btn-sm btn-light ml-2"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
          >
            {t("admin_chatbot_next")}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatbotRestaurantTable;
